import { isAttestationRequired } from '@moment/ai-chat-feature/attestation'
import {
  type BaseQueryFn,
  createApi,
  type FetchArgs,
  type FetchBaseQueryError,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react'
import LogRocket from 'logrocket'

import { getClientAuthCookieValue } from '@/app/cookies.client'
import { MomentEnv } from '@/app/moment-env'
import { Routes } from '@/app/routes'
import { reportAttestationRequired } from '@/attestation/report-attestation-required'
import { isMockMode } from '@/lib/mock-mode/is-mock-mode'
import { tagTypes } from '@/types/api/tag-types'
import { networkLogger } from '@/utils/logger/network-logger'

// Types
type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

// Extend FetchArgs to include responseType
interface ExtendedFetchArgs extends FetchArgs {
  responseType?: 'blob'
}

interface RequestDetails {
  method: RequestMethod
  url: string
  payload?: unknown
}

// Helper functions
const getRequestDetails = (
  args: string | ExtendedFetchArgs,
): RequestDetails => ({
  method:
    typeof args === 'string' ? 'GET' : (args.method as RequestMethod) || 'GET',
  url: typeof args === 'string' ? args : args.url,
  payload: typeof args === 'string' ? undefined : args.body,
})

const handleNetworkError = (
  details: RequestDetails,
  error: unknown,
  startTimestamp?: number,
) => {
  const { method, url } = details
  if (startTimestamp !== undefined) {
    networkLogger.error(method, url, error, startTimestamp)
  }
  return { error: error as FetchBaseQueryError }
}

// Base query configuration
const baseQuery = fetchBaseQuery({
  baseUrl: MomentEnv.ApiUrl,
  prepareHeaders: (headers) => {
    // Mock mode: skip auth entirely. MSW intercepts before hitting the network.
    if (isMockMode()) {
      return headers
    }
    // [PF-1072] TODO: Clean up FF = OFF path
    // Try to read auth cookie from JavaScript
    // - If readable (non-httpOnly): Set Authorization header
    // - If NOT readable (httpOnly): Browser sends cookie automatically with credentials
    //
    // This works for both old and new auth flows:
    // - Old flow: non-httpOnly cookies, we set Authorization header
    // - New flow: httpOnly cookies, browser sends them automatically
    const token = getClientAuthCookieValue()
    if (token) {
      headers.set('Authorization', `JWT ${token}`)
    }
    const sessionURL = LogRocket.sessionURL
    if (sessionURL) {
      headers.set('X-LogRocket-URL', sessionURL)
    }
    return headers
  },
  responseHandler: async (response) => {
    const contentType = response.headers.get('content-type')

    // Handle PDF responses as blobs
    if (contentType?.includes('application/pdf')) {
      return response.blob()
    }

    // Handle empty responses
    if (response.status === 204) {
      return null
    }

    // Standard JSON response handling
    return response.json()
  },
  // Include credentials (cookies) with all requests to the backend API
  // This is REQUIRED for httpOnly cookies to be sent cross-origin (e.g., preview.moment.com -> api.moment.com)
  //
  // How this works with httpOnly cookies:
  // 1. When feature flag is ON: setAuthCookie() sets httpOnly=true with domain=.moment.com
  //    (see apps/web-ui/app/auth/_lib/cookies.ts)
  // 2. Browser automatically sends the cookie with credentials: 'include'
  // 3. Requires Public-API CORS configuration (see public-api/internal/server/server.go):
  //    - Access-Control-Allow-Credentials: true  (config.AllowCredentials = true)
  //    - Access-Control-Allow-Origin: <specific origin> NOT wildcard
  //      (config.AllowOriginFunc = security.IsAllowedCORSOrigin, see public-api/internal/security/origins.go)
  credentials: 'include',
})

// Enhanced base query with auth check and logging
const baseQueryWithAuthCheck: BaseQueryFn<
  string | ExtendedFetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Sanitize payloads for synthetic proposal requests as close to the network as possible
  let effectiveArgs: string | ExtendedFetchArgs = args
  if (
    typeof args === 'object' &&
    args?.method === 'POST' &&
    typeof args.url === 'string' &&
    args.url.includes('/proposal/synthetic/')
  ) {
    // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
    const body: any = (args as ExtendedFetchArgs).body
    const sanitizedTargets = Array.isArray(body?.targets)
      ? // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        body.targets.map((t: any) => ({
          ...t,
          extra: {},
        }))
      : body?.targets

    effectiveArgs = {
      ...args,
      body: {
        ...body,
        targets: sanitizedTargets,
      },
    }
  }

  const requestDetails = getRequestDetails(effectiveArgs)
  const { method, url, payload } = requestDetails
  const startTimestamp = networkLogger.request(method, url, payload)

  try {
    const result = await baseQuery(effectiveArgs, api, extraOptions)

    // Handle authentication error
    // Don't auto-logout for 401s from authentication endpoints themselves
    // or from any endpoint when running in mock mode — unmocked endpoints
    // fall through to the real API and will return 401, which shouldn't
    // kick the harness user back to /logout.
    const isAuthEndpoint = url.startsWith('/auth/')
    if (result.error?.status === 401 && !isAuthEndpoint && !isMockMode()) {
      if (startTimestamp !== undefined) {
        networkLogger.error(
          method,
          url,
          'Auth token expired or invalid',
          startTimestamp,
        )
      }
      window.location.href = Routes.Logout.root
      return Promise.reject(result.error)
    }

    if (
      result.error?.status === 403 &&
      isAttestationRequired(result.error.data)
    ) {
      reportAttestationRequired()
    }

    // Log response or error
    if (startTimestamp !== undefined) {
      if (result.error) {
        networkLogger.error(method, url, result.error, startTimestamp)
      }
    }

    return result
  } catch (error) {
    return handleNetworkError(requestDetails, error, startTimestamp)
  }
}

// Create and export the base API slice
export const baseApiSlice = createApi({
  reducerPath: 'API',
  tagTypes,
  baseQuery: baseQueryWithAuthCheck,
  endpoints: () => ({}),
})
